// src/components/common/CurrencySwitcher.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { currencyApi } from '@/api/services'
import { ChevronDown } from 'lucide-react'

const SYMBOLS = { INR:'₹', USD:'$', EUR:'€', GBP:'£', AED:'د.إ', SGD:'S$', JPY:'¥' }
const SUPPORTED = ['INR','USD','EUR','GBP','AED','SGD','JPY']

function getStoredCurrency() {
  return localStorage.getItem('fabrinza_currency') || 'INR'
}

export default function CurrencySwitcher() {
  const [open, setOpen]         = useState(false)
  const [current, setCurrent]   = useState(getStoredCurrency)
  const qc                      = useQueryClient()

  const switchCurrency = (code) => {
    setCurrent(code)
    localStorage.setItem('fabrinza_currency', code)
    setOpen(false)
    // Invalidate all product queries so prices recalculate
    qc.invalidateQueries({ queryKey: ['products'] })
    qc.invalidateQueries({ queryKey: ['cart'] })
  }

  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-border bg-white text-xs font-bold hover:border-pink transition-colors">
        <span>{SYMBOLS[current] || current}</span>
        <span>{current}</span>
        <ChevronDown size={11} className="text-muted" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1.5 bg-white border border-border rounded-2xl shadow-card-md z-50 min-w-[110px] overflow-hidden">
            {SUPPORTED.map(code => (
              <button key={code} onClick={() => switchCurrency(code)}
                className={`w-full text-left flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-surface transition-colors
                  ${current === code ? 'font-bold text-pink bg-pink-light' : 'font-medium text-gray-700'}`}>
                <span className="w-5 text-center">{SYMBOLS[code]}</span>
                <span>{code}</span>
                {current === code && <span className="ml-auto text-xs">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Hook to get current currency for price formatting
export function useCurrency() {
  const [currency] = useState(getStoredCurrency)
  const symbol = SYMBOLS[currency] || '₹'

  const format = (amount) => {
    const num = parseFloat(amount) || 0
    return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
  }

  return { currency, symbol, format }
}
